import { ClearFiltersIcon, SearchIcon } from '@/components/icons'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { WorkflowDefinitionFilters } from '@/types'
import { WORKFLOW_DEFINITION_CATEGORIES, CATEGORY_LABELS } from '@/types'
import { LayoutGrid, List, X } from 'lucide-react'

interface WorkflowDefinitionsFilterBarProps {
  filters: WorkflowDefinitionFilters
  onFilterChange: (key: keyof WorkflowDefinitionFilters, value: string) => void
  onClearAll: () => void
  viewMode: 'grid' | 'list'
  onViewModeChange: (mode: 'grid' | 'list') => void
}

export function WorkflowDefinitionsFilterBar({ filters, onFilterChange, onClearAll, viewMode, onViewModeChange }: WorkflowDefinitionsFilterBarProps) {
  const hasActiveFilters = filters.search !== '' || filters.status !== 'active' || filters.type !== 'all' || filters.category !== 'all'

  return (
    <div className="flex flex-1 items-center gap-4">
      <div className="relative w-[440px]">
        <SearchIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-foreground" />
        <Input
          placeholder="Search"
          value={filters.search}
          onChange={(e) => onFilterChange('search', e.target.value)}
          className="h-10 rounded-lg border-input pl-10 pr-9"
          aria-label="Search workflow definitions"
          data-testid="workflow-search-input"
        />
        {filters.search && (
          <button
            type="button"
            onClick={() => onFilterChange('search', '')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <Select
        value={filters.status}
        onValueChange={(value) => onFilterChange('status', value)}
      >
        <SelectTrigger className="w-[160px] h-10 rounded-lg border-input" aria-label="Filter by status">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Statuses</SelectItem>
          <SelectItem value="active">Active</SelectItem>
          <SelectItem value="inactive">Inactive</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={filters.category}
        onValueChange={(value) => onFilterChange('category', value)}
      >
        <SelectTrigger className="w-[180px] h-10 rounded-lg border-input" aria-label="Filter by category">
          <SelectValue placeholder="Category" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Categories</SelectItem>
          {WORKFLOW_DEFINITION_CATEGORIES.map((cat) => (
            <SelectItem key={cat} value={cat}>{CATEGORY_LABELS[cat]}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.type}
        onValueChange={(value) => onFilterChange('type', value)}
      >
        <SelectTrigger className="w-[160px] h-10 rounded-lg border-input" aria-label="Filter by type">
          <SelectValue placeholder="Type" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Types</SelectItem>
          <SelectItem value="system">System</SelectItem>
          <SelectItem value="custom">Custom</SelectItem>
        </SelectContent>
      </Select>

      <Button
        type="button"
        variant="ghost"
        onClick={onClearAll}
        disabled={!hasActiveFilters}
        className="flex items-center gap-1.5 text-sm font-medium"
        aria-label="Clear all filters"
      >
        <ClearFiltersIcon className="h-4 w-4" />
        Clear All
      </Button>

      <div className="ml-auto flex items-center rounded-md border border-input p-1 gap-1 bg-toolbar">
        <Button
          variant={viewMode === 'list' ? 'secondary' : 'ghost'}
          size="icon"
          className={`rounded border-0 h-10 w-10 ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : ''}`}
          onClick={() => onViewModeChange('list')}
          aria-label="Switch to list view"
          data-testid="view-mode-list"
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
          size="icon"
          className={`rounded border-0 h-10 w-10 ${viewMode === 'grid' ? 'bg-primary text-primary-foreground' : ''}`}
          onClick={() => onViewModeChange('grid')}
          aria-label="Switch to grid view"
          data-testid="view-mode-grid"
        >
          <LayoutGrid className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
